import { ref, computed, onMounted } from 'vue'

export const useTimezone = () => {
  const currentTimezone = ref('')
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

  onMounted(() => {
    if (process.client) {
      // Load from localStorage or use browser default
      currentTimezone.value = localStorage.getItem('userTimezone') || browserTimezone
      
      // Listen for timezone changes
      window.addEventListener('timezoneChanged', (event: Event) => {
        const customEvent = event as CustomEvent
        currentTimezone.value = customEvent.detail.timezone
      })
    }
  })

  // Format date in user's timezone
  const formatDateInTimezone = (date: string | Date, options: Intl.DateTimeFormatOptions = {}) => {
    const timezone = currentTimezone.value || browserTimezone
    
    const defaultOptions: Intl.DateTimeFormatOptions = {
      timeZone: timezone,
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      ...options
    }

    try {
      return new Intl.DateTimeFormat('en-US', defaultOptions).format(new Date(date))
    } catch (e) {
      console.error('Error formatting date:', e)
      return new Date(date).toLocaleDateString()
    }
  }

  // Format time in user's timezone
  const formatTimeInTimezone = (date: string | Date, options: Intl.DateTimeFormatOptions = {}) => {
    const timezone = currentTimezone.value || browserTimezone
    
    const defaultOptions: Intl.DateTimeFormatOptions = {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      ...options
    }

    try {
      return new Intl.DateTimeFormat('en-US', defaultOptions).format(new Date(date))
    } catch (e) {
      console.error('Error formatting time:', e)
      return new Date(date).toLocaleTimeString()
    }
  }

  // Format full date and time in user's timezone
  const formatFullDateInTimezone = (date: string | Date, options: Intl.DateTimeFormatOptions = {}) => {
    const timezone = currentTimezone.value || browserTimezone
    
    const defaultOptions: Intl.DateTimeFormatOptions = {
      timeZone: timezone,
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short',
      ...options
    }

    try {
      return new Intl.DateTimeFormat('en-US', defaultOptions).format(new Date(date))
    } catch (e) {
      console.error('Error formatting full date:', e)
      return new Date(date).toLocaleString()
    }
  }

  // Check if date is today in user's timezone
  const isTodayInTimezone = (date: string | Date) => {
    const timezone = currentTimezone.value || browserTimezone
    
    try {
      const today = new Date()
      const inputDate = new Date(date)
      
      const todayInTz = new Intl.DateTimeFormat('en-CA', { timeZone: timezone }).format(today)
      const inputDateInTz = new Intl.DateTimeFormat('en-CA', { timeZone: timezone }).format(inputDate)
      
      return todayInTz === inputDateInTz
    } catch (e) {
      console.error('Error checking if date is today:', e)
      return false
    }
  }

  // Check if date is overdue in user's timezone
  const isOverdueInTimezone = (date: string | Date) => {
    const timezone = currentTimezone.value || browserTimezone
    
    try {
      const today = new Date()
      const inputDate = new Date(date)
      
      // Get start of day in user's timezone
      const todayInTz = new Date(new Intl.DateTimeFormat('en-CA', { timeZone: timezone }).format(today))
      const inputDateInTz = new Date(new Intl.DateTimeFormat('en-CA', { timeZone: timezone }).format(inputDate))
      
      return inputDateInTz < todayInTz
    } catch (e) {
      console.error('Error checking if date is overdue:', e)
      return false
    }
  }

  // Get days difference in user's timezone
  const getDaysDifferenceInTimezone = (date1: string | Date, date2: string | Date = new Date()) => {
    const timezone = currentTimezone.value || browserTimezone
    
    try {
      const d1 = new Date(new Intl.DateTimeFormat('en-CA', { timeZone: timezone }).format(new Date(date1)))
      const d2 = new Date(new Intl.DateTimeFormat('en-CA', { timeZone: timezone }).format(new Date(date2)))
      
      const diffTime = d2.getTime() - d1.getTime()
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    } catch (e) {
      console.error('Error calculating days difference:', e)
      return 0
    }
  }

  // Get current date in user's timezone
  const getCurrentDateInTimezone = () => {
    const timezone = currentTimezone.value || browserTimezone
    
    try {
      return new Date(new Intl.DateTimeFormat('en-CA', { timeZone: timezone }).format(new Date()))
    } catch (e) {
      console.error('Error getting current date in timezone:', e)
      return new Date()
    }
  }

  // Set timezone function
  const setTimezone = (timezone: string) => {
    if (process.client) {
      currentTimezone.value = timezone
      localStorage.setItem('userTimezone', timezone)
      
      // Emit event for other components to listen to
      window.dispatchEvent(new CustomEvent('timezoneChanged', { 
        detail: { timezone } 
      }))
    }
  }

  // Get timezone abbreviation (e.g., "EST", "PDT", "GMT")
  const getTimezoneAbbreviation = (timezone?: string) => {
    const tz = timezone || currentTimezone.value || browserTimezone
    
    try {
      const now = new Date()
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        timeZoneName: 'short'
      })
      
      const parts = formatter.formatToParts(now)
      const timezonePart = parts.find(part => part.type === 'timeZoneName')
      
      return timezonePart?.value || tz.split('/').pop()?.replace('_', ' ') || tz
    } catch (e) {
      console.error('Error getting timezone abbreviation:', e)
      return tz.split('/').pop()?.replace('_', ' ') || tz
    }
  }

  return {
    currentTimezone: computed(() => currentTimezone.value || browserTimezone),
    setTimezone,
    getTimezoneAbbreviation,
    formatDateInTimezone,
    formatTimeInTimezone,
    formatFullDateInTimezone,
    isTodayInTimezone,
    isOverdueInTimezone,
    getDaysDifferenceInTimezone,
    getCurrentDateInTimezone
  }
}
